<template lang="pug">

div

    .ui.item.dropdown.compact
        i.icon.ellipsis.vertical
        .menu
            router-link.item(v-if='showEditLink' v-bind:to='toViewRoute')
                i.edit.icon
                |  Bearbeiten
            .item(v-if='canCancel' v-bind:class='{ disabled: cancelDisabled }' v-on:click='!cancelDisabled && cancelProduct()')
                i.ban.icon
                |  Stornieren
            .item(v-on:click='showHistory')
                i.history.icon
                |  Verlauf anzeigen
            slot
            .item(v-if='canArchive' v-on:click='archiveProduct')
                i.archive.icon
                |  Archivieren
            .item(v-on:click='saveAsTemplate()')
                i.bookmark.icon
                |  Als Vorlage speichern
            .item(v-on:click='duplicateProduct')
                i.copy.icon
                |  Duplizieren

</template>

<script>
    import * as R from 'ramda'
    import store from '@/store'
    import { PRODUCT_TYPE_ID_TO_FORMS_KEY } from '@/utils/product/type'

    export default {
        name: 'product-action-dropdown',

        props: {

            productModel: {
                type: Object,
                required: true
            },

            showEditLink: {
                type: Boolean,
                default: false
            },

            variantModels: {
                type: Array,
                required: true
            },

        },

        computed: {

            canCancel() {
                const { status, isArchived } = this.productModel
                if (isArchived || status === 'CANCELED') return false
                if (status === 'IN_PRODUCTION') return this.$auth.activeRole === 'ADMIN'
                return true
            },

            cancelDisabled() {
                return ['IN_PRINT', 'PRODUCTION_RELEASE', 'IN_PRODUCTION', 'DELIVERED', 'QA', 'COMPLETED'].includes(this.productModel.status)
            },

            canArchive() {
                const { status, isArchived } = this.productModel
                return !isArchived && (status === 'COMPLETED' || status === 'CANCELED')
            },

            toViewRoute() {
                return {
                    name: 'products/view',
                    params: {
                        id: this.productModel.id,
                        variantId: R.path([0, 'id'], this.variantModels)
                    }
                }
            },

        },

        mounted() {
            $(this.$el).find('.dropdown').dropdown()
        },

        methods: {

            async cancelProduct() {
                const confirmed = await this.$growl.confirm(
                    `Produkt "${this.productModel.name}" wirklich stornieren? Alle offenen Jobs werden dabei abgelehnt.`
                )
                if (!confirmed) return

                try {
                    await store.dispatch('products/cancel', { id: this.productModel.id })
                    this.$root.$emit('products:refresh')
                    this.$growl.warning(`"${this.productModel.name}" wurde storniert`)
                } catch (e) {
                    this.$growl.fatal('Produkt konnte nicht storniert werden', e)
                }
            },

            async archiveProduct() {
                const confirmed = await this.$growl.confirm(
                    `Produkt "${this.productModel.name}" archivieren?`
                )
                if (!confirmed) return

                try {
                    await store.dispatch('products/archive', { id: this.productModel.id })
                    this.$root.$emit('products:refresh')
                    this.$growl.success('Produkt wurde archiviert')
                } catch (e) {
                    this.$growl.fatal('Produkt konnte nicht archiviert werden', e)
                }
            },

            showHistory() {
                this.$productHistory.show(this.productModel.id, this.productModel.name)
            },

            async saveAsTemplate() {
                const templateName = window.prompt('Name der Vorlage:', this.productModel.name)
                if (templateName === null) return

                await Promise.all(
                    this.variantModels.map(v =>
                        this.$store.dispatch('fields/loadAllByVariantId', { variantId: v.id })
                    )
                )

                const variants = this.variantModels.map((v, index) => {
                    const printings = [...(v.printings || [])].sort((a, b) => a - b)
                    const fieldModels = this.$store.getters['fields/allByVariantId'](v.id)
                    const fields = fieldModels.map(f => ({ key: f.key, value: f.value }))

                    return {
                        number:   index + 1,
                        sorts:    v.sorts ? [...v.sorts] : [],
                        printings,
                        fields,
                    }
                })

                const model = {
                    name:     templateName,
                    type:     this.productModel.type,
                    variants,
                }

                try {
                    await this.$store.dispatch('productTemplates/create', { model })
                    this.$growl.success(`"${templateName}" wurde als Vorlage gespeichert`)
                } catch (e) {
                    this.$growl.fatal('Vorlage konnte nicht gespeichert werden', e)
                }
            },

            async duplicateProduct() {
                const key = PRODUCT_TYPE_ID_TO_FORMS_KEY[this.productModel.type]
                if (!key) {
                    this.$growl.fatal('Produkt konnte nicht dupliziert werden: Unbekannter Produkttyp')
                    return
                }

                await Promise.all(
                    this.variantModels.map(v =>
                        this.$store.dispatch('fields/loadAllByVariantId', { variantId: v.id })
                    )
                )

                const variants = this.variantModels.map((v, index) => {

                    const printings = [...(v.printings || [])].sort((a, b) => a - b)

                    const fieldModels = this.$store.getters['fields/allByVariantId'](v.id)
                    const fields = fieldModels.reduce((acc, f) => {
                        acc[f.key] = f.value
                        return acc
                    }, {})

                    return {
                        number:    index + 1,
                        printing1: printings[0] || null,
                        printing2: printings[1] || null,
                        printing3: printings[2] || null,
                        printing4: printings[3] || null,
                        printing5: printings[4] || null,
                        sorts:     v.sorts ? [...v.sorts] : [],
                        fields,
                    }
                })

                const draft = {
                    type:              this.productModel.type,
                    company:           this.productModel.company,
                    name:              this.productModel.name,
                    campaign:          this.productModel.campaign    || null,
                    freeText1:         this.productModel.freeText1   || null,
                    freeText2:         this.productModel.freeText2   || null,
                    freeText3:         this.productModel.freeText3   || null,
                    mediaProduction:   this.productModel.mediaProduction || null,
                    deliveryDate:      this.productModel.deliveryDate || null,
                    proof:             { isWanted: 'NO' },
                    files:             [],
                    shippingAddresses: [{}],
                    variants,
                }

                this.$store.commit('products/RESET_DRAFT')
                this.$store.commit('products/REPLACE_DRAFT', { draft })

                this.$router.push({
                    name:   'products/create/step2',
                    params: { key },
                })
            },

        },

    }
</script>
